// Shared validation helpers for quantity-tier arrays (used by both
// UpsertCorporatePartCost and UpsertCorporatePartPrice schemas).

export const findOverlappingItemIndex = (
  items: { minQuantity?: number | string; maxQuantity?: number | string }[]
): number | null => {
  const ranges = items
    .map((item, originalIndex) => ({
      originalIndex,
      min:
        item.minQuantity != null && (item.minQuantity as unknown) !== ''
          ? Number(item.minQuantity)
          : null,
      max:
        item.maxQuantity != null && (item.maxQuantity as unknown) !== ''
          ? Number(item.maxQuantity)
          : Number.POSITIVE_INFINITY,
    }))
    .filter((r): r is { originalIndex: number; min: number; max: number } => r.min !== null)
    .sort((a, b) => a.min - b.min);

  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i].min < ranges[i - 1].max) {
      return ranges[i].originalIndex;
    }
  }

  return null;
};

// Returns the index of the last tier IF its maxQuantity is bounded (i.e., the
// "open-ended last tier" rule is violated); otherwise null.
export const findLastBoundedItemIndex = (
  items: { maxQuantity?: number | string }[]
): number | null => {
  if (!items || items.length === 0) {
    return null;
  }

  const lastIndex = items.length - 1;
  const lastMax = items[lastIndex]?.maxQuantity;

  if (lastMax == null || (lastMax as unknown) === '') {
    return null;
  }

  return lastIndex;
};

// Returns the originalIndex of the first tier whose minQuantity leaves a gap
// after the previous tier's maxQuantity (e.g., [1..10], [12..20] — 11 uncovered).
// Open-ended previous tiers are skipped (the open-ended-last-tier rule handles those).
export const findGapItemIndex = (
  items: { minQuantity?: number | string; maxQuantity?: number | string }[]
): number | null => {
  const ranges = items
    .map((item, originalIndex) => ({
      originalIndex,
      min:
        item.minQuantity != null && (item.minQuantity as unknown) !== ''
          ? Number(item.minQuantity)
          : null,
      max:
        item.maxQuantity != null && (item.maxQuantity as unknown) !== ''
          ? Number(item.maxQuantity)
          : null,
    }))
    .filter((r): r is { originalIndex: number; min: number; max: number | null } => r.min !== null)
    .sort((a, b) => a.min - b.min);

  for (let i = 1; i < ranges.length; i++) {
    const prevMax = ranges[i - 1].max;

    if (prevMax == null) {
      continue;
    }
    if (ranges[i].min > prevMax) {
      return ranges[i].originalIndex;
    }
  }

  return null;
};
